import type { ControlFrameConfig, ViewConfig } from "@kontextwerk/control-web-frame"
import { kehrblattActivityCatalog } from "./_logic/activityCatalog"
import {
  WorkOrderDetailSubview as WorkOrderDetail,
  workspaceConfig as workOrderWorkspace,
} from "../OrgaView/pages/WorkOrders/subviews/WorkOrderDetail"
import {
  PropertyDetailSubview as PropertyDetail,
  workspaceConfig as propertyWorkspace,
} from "../OrgaView/pages/Administration/subviews/Properties/subviews/PropertyDetail"
import {
  CustomerDetailSubview as CustomerDetail,
  workspaceConfig as customerWorkspace,
} from "../OrgaView/pages/Administration/subviews/Customers/subviews/CustomerDetail"
import { OrgaViewFrame, workspaceConfig as organizationWorkspace } from "../OrgaView"
import { UserSubview as UserViewFrame } from "../OrgaView/subviews/User"
import {
  LearningSubview,
  workspaceConfig as learningWorkspace,
} from "../OrgaView/subviews/Learning"
import { buildUserProfilePages } from "../OrgaView/subviews/User/navigation"
import OrgPresentationViewFrame from "../OrgPresentationView"
import ShortCodeRedirectViewFrame from "../ShortCodeRedirectView"
import { AdminViewFrame, workspaceConfig as adminWorkspace } from "./admin-view-index"

const trimPath = (value: string | null | undefined) => (value ?? "").replace(/\/+$/, "") || "/"
const uuidPattern = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
const asUuid = (value: string | null | undefined) =>
  value && uuidPattern.test(value) ? value : null
const decodeSegment = (value: string | null | undefined) => {
  if (!value) return null

  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

const detailViews = [
  {
    id: "learning",
    workspace: learningWorkspace,
    component: LearningSubview,
    requiresAuth: true,
    scope: "organization",
    priority: 91,
    match: (ctx) => /^\/organizations\/[^/]+\/learn(?:\/|$)/.test(trimPath(ctx.path)),
    getProps: (ctx) => {
      const parts = trimPath(ctx.path).split("/").filter(Boolean)
      const organizationIdentifier = ctx.orgSlug ?? decodeSegment(parts[1]) ?? ""

      return {
        organizationId: organizationIdentifier,
        organizationSlug: organizationIdentifier,
        organizationResolvedId: ctx.orgId ?? asUuid(organizationIdentifier),
      }
    },
    getKey: (ctx) => {
      const parts = trimPath(ctx.path).split("/").filter(Boolean)

      return `learning:${ctx.orgSlug ?? decodeSegment(parts[1]) ?? "none"}`
    },
  },
  {
    id: "work-order-detail",
    workspace: workOrderWorkspace,
    component: WorkOrderDetail,
    requiresAuth: true,
    scope: "organization",
    priority: 90,
    match: (ctx) => /^\/organizations\/[^/]+\/work-orders\/[^/]+/.test(trimPath(ctx.path)),
    getProps: (ctx) => {
      const parts = trimPath(ctx.path).split("/").filter(Boolean)
      const organizationIdentifier = ctx.orgSlug ?? decodeSegment(parts[1]) ?? ""

      return {
        organizationId: organizationIdentifier,
        organizationSlug: organizationIdentifier,
        organizationResolvedId: ctx.orgId ?? asUuid(organizationIdentifier),
        workOrderId: decodeSegment(parts[3]) ?? "",
      }
    },
    getKey: (ctx) => {
      const parts = trimPath(ctx.path).split("/").filter(Boolean)

      return `work-order:${ctx.orgSlug ?? decodeSegment(parts[1]) ?? "global"}:${decodeSegment(parts[3]) ?? "none"}`
    },
  },
  {
    id: "property-detail",
    workspace: propertyWorkspace,
    component: PropertyDetail,
    requiresAuth: true,
    scope: "organization",
    priority: 88,
    match: (ctx) => /^\/organizations\/[^/]+\/addresses\/[^/]+/.test(trimPath(ctx.path)),
    getProps: (ctx) => {
      const parts = trimPath(ctx.path).split("/").filter(Boolean)
      const organizationIdentifier = ctx.orgSlug ?? decodeSegment(parts[1]) ?? ""

      return {
        organizationId: organizationIdentifier,
        organizationSlug: organizationIdentifier,
        organizationResolvedId: ctx.orgId ?? asUuid(organizationIdentifier),
        propertyId: decodeSegment(parts[3]) ?? "",
      }
    },
    getKey: (ctx) => {
      const parts = trimPath(ctx.path).split("/").filter(Boolean)

      return `property:${ctx.orgSlug ?? decodeSegment(parts[1]) ?? "global"}:${decodeSegment(parts[3]) ?? "none"}`
    },
  },
  {
    id: "customer-detail",
    workspace: customerWorkspace,
    component: CustomerDetail,
    requiresAuth: true,
    scope: "organization",
    priority: 87,
    match: (ctx) => /^\/organizations\/[^/]+\/customers\/[^/]+/.test(trimPath(ctx.path)),
    getProps: (ctx) => {
      const parts = trimPath(ctx.path).split("/").filter(Boolean)
      const organizationIdentifier = ctx.orgSlug ?? decodeSegment(parts[1]) ?? ""

      return {
        organizationId: organizationIdentifier,
        organizationSlug: organizationIdentifier,
        organizationResolvedId: ctx.orgId ?? asUuid(organizationIdentifier),
        customerId: decodeSegment(parts[3]) ?? "",
      }
    },
    getKey: (ctx) => {
      const parts = trimPath(ctx.path).split("/").filter(Boolean)

      return `customer:${ctx.orgSlug ?? decodeSegment(parts[1]) ?? "global"}:${decodeSegment(parts[3]) ?? "none"}`
    },
  },
] as unknown as ViewConfig[]

const presentationView = {
  id: "presentation",
  component: OrgPresentationViewFrame,
  requiresAuth: false,
  scope: "unauthenticated",
  priority: 102,
  match: (ctx) => /^\/presentation\/[^/]+/.test(trimPath(ctx.path)),
  getProps: (ctx) => {
    const parts = trimPath(ctx.path).split("/").filter(Boolean)

    return {
      orgIdentifier: decodeSegment(parts[1]) ?? "",
    }
  },
  getKey: (ctx) => {
    const parts = trimPath(ctx.path).split("/").filter(Boolean)

    return `presentation:${decodeSegment(parts[1]) ?? "none"}`
  },
} as unknown as ViewConfig

const shortCodeRedirectView = {
  id: "public-short-code",
  component: ShortCodeRedirectViewFrame,
  requiresAuth: false,
  scope: "unauthenticated",
  priority: 104,
  match: (ctx) => /^\/c\/[^/]+/.test(trimPath(ctx.path)),
  getProps: (ctx) => {
    const parts = trimPath(ctx.path).split("/").filter(Boolean)

    return {
      code: decodeSegment(parts[1]) ?? "",
    }
  },
  getKey: (ctx) => {
    const parts = trimPath(ctx.path).split("/").filter(Boolean)

    return `public-short-code:${decodeSegment(parts[1]) ?? "none"}`
  },
} as unknown as ViewConfig

const planCardPricePresentation = {
  priceDetailMetricOrder: [
    "work_order_completion",
    "chat_conversation",
    "mobile_message_delivery",
    "automatic_letter_delivery",
    "voice_session",
  ],
  hiddenPriceDetailMetrics: [],
  additionalPriceDetailLines: [
    {
      key: "mail_delivery_free",
      label: "Preis pro Mail",
      value: "0 Credits (0,00 € netto) pro Mail",
      insertAfterMetric: "work_order_completion",
    },
  ],
  usageNote:
    "Mails sind kostenlos. Mobile Nachrichten und automatisch versendete Briefe werden verbrauchsabhängig in Credits abgerechnet. Der Auftrag wird erst nach Fertigstellung berechnet, die Pauschale beeinhaltet beliebig viele Terminverschiebungen.",
  usageNotesBySlug: {
    kehrchat:
      "Mails sind kostenlos. Mobile Nachrichten und automatisch versendete Briefe werden verbrauchsabhängig in Credits abgerechnet. Der Auftrag wird erst nach Fertigstellung berechnet, die Pauschale beeinhaltet beliebig viele Terminverschiebungen. Chatbot-Unterhaltungen werden in KehrChat zusätzlich mit 15 Credits pro Unterhaltung berechnet.",
    kehrtalk:
      "Mails sind kostenlos. Mobile Nachrichten und automatisch versendete Briefe werden verbrauchsabhängig in Credits abgerechnet. Der Auftrag wird erst nach Fertigstellung berechnet, die Pauschale beeinhaltet beliebig viele Terminverschiebungen. Chatbot-Unterhaltungen werden in KehrTalk zusätzlich mit 15 Credits pro Unterhaltung berechnet. Telefonie-Minuten werden in KehrTalk zusätzlich mit 21 Credits pro Minute berechnet.",
  },
}

export const controlFrameConfig: ControlFrameConfig = {
  profilePages: buildUserProfilePages,
  theme: "KehrBlatt",
  activity: { catalog: kehrblattActivityCatalog },
  guardId: "control-frame:overlay",
  sentry: {
    projectId: 5,
    dsn: "https://489eef146f57a726d10c7f2441b376ae@sentry-console.kontextwerk.agency/5",
  },
  signup: {
    enabled: true,
    requireActiveSubscription: true,
  },
  features: {
    feedback: true,
  },
  billing: {
    mode: "stripe",
    expiredSubscriptionRouting: "banner_only",
    planCardPresentation: planCardPricePresentation,
  } as NonNullable<ControlFrameConfig["billing"]> & {
    planCardPresentation: typeof planCardPricePresentation
  },
  assist: {
    projectName: "kehrblatt-control-assist",
  },
  support: {
    cookieConsentTags: ["youtube"],
  },
  views: {
    enableSignup: true,
    overrides: {
      "admin-home": {
        component: AdminViewFrame,
        workspace: adminWorkspace,
      },
      organization: {
        component: OrgaViewFrame,
        workspace: organizationWorkspace,
      },
      user: {
        component: UserViewFrame,
      },
    },
    extras: [...detailViews, presentationView, shortCodeRedirectView],
    publicRouteMatcher: (path) => {
      const normalizedPath = trimPath(path)

      return /^\/presentation\/[^/]+/.test(normalizedPath) || /^\/c\/[^/]+/.test(normalizedPath)
    },
  },
}
